import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Check, X, User, Briefcase, FileText, Banknote,
  ShieldCheck, ShoppingCart, MessageSquare, Send, Package, Tag, Hash
} from 'lucide-react';

const statusBadgeStyle = (status) => {
  const map = {
    Approved: { bg: 'rgba(34,211,165,0.1)', color: '#22D3A5', border: 'rgba(34,211,165,0.3)' },
    Rejected: { bg: 'rgba(248,113,113,0.1)', color: '#F87171', border: 'rgba(248,113,113,0.3)' },
  };
  const s = map[status] || { bg: 'rgba(245,158,11,0.1)', color: '#FBBF24', border: 'rgba(245,158,11,0.3)' };
  return { padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, letterSpacing: '0.05em', background: s.bg, color: s.color, border: `1px solid ${s.border}` };
};

const priorityColor = (p) => {
  if (p === 'Urgent') return { bg: 'rgba(239,68,68,0.12)', color: '#F87171', border: 'rgba(239,68,68,0.25)' };
  if (p === 'High')   return { bg: 'rgba(245,158,11,0.12)', color: '#FBBF24', border: 'rgba(245,158,11,0.25)' };
  return { bg: 'rgba(59,130,246,0.1)', color: '#60A5FA', border: 'rgba(59,130,246,0.2)' };
};

// ─── Petty Cash Card (unchanged) ───
const PettyCashCard = ({ req, getJobName, formatTime, onStatus }) => (
  <div style={{ background: '#0D1120', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <div style={{ fontSize: '16px', fontWeight: 800, color: '#E2E8F0', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Briefcase size={14} color="#60A5FA" /> {getJobName(req.job_order_id)}
        </div>
        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontFamily: "'DM Mono', monospace" }}>#JO-{req.job_order_id}</span>
      </div>
      <div style={{ display: 'flex', gap: '15px', fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><User size={12} /> {req.team_id}</div>
        <div style={{ color: 'rgba(255,255,255,0.3)' }}>{formatTime(req.requested_at)}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '13px', background: '#111827', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.03)' }}>
        <FileText size={14} color="rgba(255,255,255,0.3)" style={{ marginTop: '2px', flexShrink: 0 }} />
        <span style={{ fontStyle: req.reason ? 'normal' : 'italic', color: req.reason ? '#E2E8F0' : 'rgba(255,255,255,0.3)' }}>{req.reason || 'No details provided.'}</span>
      </div>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: '30px', minWidth: '140px' }}>
      <div style={{ fontSize: '24px', fontWeight: 900, color: '#F59E0B', fontFamily: "'DM Mono', monospace", marginBottom: '12px' }}>Rs.{req.amount}</div>
      {req.status === 'Pending' ? (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => onStatus(req.id, 'Rejected')} style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700 }}>
            <X size={14} /> Reject
          </button>
          <button onClick={() => onStatus(req.id, 'Approved')} style={{ background: 'rgba(34,211,165,0.1)', border: '1px solid rgba(34,211,165,0.3)', color: '#22D3A5', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700 }}>
            <Check size={14} /> Approve
          </button>
        </div>
      ) : (
        <div style={statusBadgeStyle(req.status)}>{req.status.toUpperCase()}</div>
      )}
    </div>
  </div>
);

// ─── Purchase Request Card ───
const PurchaseCard = ({ req, formatTime, onStatus, onComment }) => {
  const [commentText, setCommentText] = useState(req.finance_comment || '');
  const [showComment, setShowComment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pc = priorityColor(req.priority);

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment(req.id, commentText);
    setShowComment(false);
    setSubmitting(false);
  };

  return (
    <div style={{ background: '#0D1120', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '20px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

        {/* Left info */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#E2E8F0', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Package size={14} color="#F59E0B" /> {req.item_name || 'Unnamed Item'}
            </div>
            <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: pc.bg, color: pc.color, border: `1px solid ${pc.border}` }}>
              {req.priority || 'NORMAL'}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 15, fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Hash size={12} /> Qty: {req.quantity}</div>
            {req.vendor && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Tag size={12} /> {req.vendor}</div>}
            <div style={{ color: 'rgba(255,255,255,0.3)' }}>{formatTime(req.requested_at)}</div>
          </div>

          {req.reason && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, background: '#111827', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.03)', marginBottom: 10 }}>
              <FileText size={14} color="rgba(255,255,255,0.3)" style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ color: '#E2E8F0' }}>{req.reason}</span>
            </div>
          )}

          {/* Existing finance comment */}
          {req.finance_comment && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', fontSize: 12, color: '#93C5FD', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <MessageSquare size={13} style={{ marginTop: 2, flexShrink: 0 }} />
              <div><span style={{ fontWeight: 700, marginRight: 6 }}>Finance note:</span>{req.finance_comment}</div>
            </div>
          )}
        </div>

        {/* Right: amount + actions */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: 30, minWidth: 150 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#F59E0B', fontFamily: "'DM Mono', monospace", marginBottom: 12 }}>
            Rs.{req.estimated_cost}
          </div>

          {req.status === 'Pending' ? (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button onClick={() => onStatus(req.id, 'Rejected')} style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700 }}>
                <X size={13} /> Reject
              </button>
              <button onClick={() => onStatus(req.id, 'Approved')} style={{ background: 'rgba(34,211,165,0.1)', border: '1px solid rgba(34,211,165,0.3)', color: '#22D3A5', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700 }}>
                <Check size={13} /> Approve
              </button>
            </div>
          ) : (
            <div style={{ ...statusBadgeStyle(req.status), marginBottom: 10 }}>{req.status.toUpperCase()}</div>
          )}

          {/* Comment toggle */}
          <button onClick={() => setShowComment(!showComment)} style={{ background: showComment ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', color: '#60A5FA', padding: '7px 13px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700 }}>
            <MessageSquare size={12} /> {req.finance_comment ? 'Edit Comment' : 'Add Comment'}
          </button>
        </div>
      </div>

      {/* Comment Box */}
      {showComment && (
        <div style={{ marginTop: 14, padding: 14, background: '#111827', borderRadius: 10, border: '1px solid rgba(59,130,246,0.2)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Finance Comment</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <textarea
              placeholder="Add a note for the operations team..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              style={{ flex: 1, background: '#0D1120', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#E2E8F0', fontSize: 13, padding: '10px 12px', outline: 'none', resize: 'vertical', minHeight: 60, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }}
            />
            <button
              onClick={handleCommentSubmit}
              disabled={submitting || !commentText.trim()}
              style={{ background: submitting || !commentText.trim() ? 'rgba(59,130,246,0.3)' : '#3B82F6', border: 'none', color: '#fff', borderRadius: 8, padding: '0 16px', cursor: submitting || !commentText.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, alignSelf: 'flex-end', height: 38 }}
            >
              <Send size={13} /> {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════
// ─── MAIN DASHBOARD ───
// ═══════════════════════════════════════
const FinanceDashboard = () => {
  const [activeTab, setActiveTab]       = useState('petty-cash');
  const [pettyCashReqs, setPettyCashReqs] = useState([]);
  const [purchaseReqs, setPurchaseReqs]   = useState([]);
  const [jobOrders, setJobOrders]         = useState([]);
  const [statusFilter, setStatusFilter]   = useState('Pending');
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);

      // Job Orders (for petty cash name lookup)
      try {
        let jobRes;
        try { jobRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/job-orders/ops-job-orders`); }
        catch { jobRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/active-dropdown`); }
        const raw = jobRes.data;
        let jobs = [];
        if (raw && Array.isArray(raw.data)) jobs = raw.data;
        else if (raw && Array.isArray(raw.rows)) jobs = raw.rows;
        else if (Array.isArray(raw)) jobs = raw;
        setJobOrders(jobs);
      } catch (e) { console.error('Job Orders error:', e); }

      // Petty Cash — existing endpoint (unchanged)
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/job-orders/finance/petty-cash-all`);
        setPettyCashReqs(res.data.data || []);
      } catch (e) { console.error('Petty cash error:', e); }

      // Purchase Requests — updated endpoint
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/purchase-request/get-requests`);
        setPurchaseReqs(res.data.data || []);
      } catch (e) { console.error('Purchase requests error:', e); }

      setLoading(false);
    };
    fetchAll();
  }, []);

  // Petty Cash status update — existing endpoint (unchanged)
  const handlePettyCashStatus = async (id, status) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/job-orders/finance/petty-cash-status`, { id, status });
      setPettyCashReqs(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch { alert('Failed to update status.'); }
  };

  // Purchase Request status update — updated endpoint
  const handlePurchaseStatus = async (id, status) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/purchase-request/set-approval`, { id, status });
      setPurchaseReqs(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch { alert('Failed to update status.'); }
  };

  // Purchase Request comment — updated endpoint
  const handlePurchaseComment = async (id, comment) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/puchase-request/set-comment`, { id, comment });
      setPurchaseReqs(prev => prev.map(r => r.id === id ? { ...r, finance_comment: comment } : r));
    } catch { alert('Failed to save comment.'); }
  };

  const getJobName = (jobId) => {
    const j = jobOrders.find(o => (o.id || o.job_id) == jobId);
    return j ? (j.customer_name || j.client_name || `JO-${jobId}`) : `JO-${jobId}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ', ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const filteredPetty    = pettyCashReqs.filter(r => statusFilter === 'All' || r.status === statusFilter);
  const filteredPurchase = purchaseReqs.filter(r => statusFilter === 'All' || r.status === statusFilter);
  const pendingPetty     = pettyCashReqs.filter(r => r.status === 'Pending').length;
  const pendingPurchase  = purchaseReqs.filter(r => r.status === 'Pending').length;

  return (
    <div style={{ padding: '40px 20px', background: '#080C18', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#3B82F6', letterSpacing: '0.1em', marginBottom: 4 }}>FINANCE DEPARTMENT</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck size={28} color="#22D3A5" /> Finance Approvals
          </h1>
        </div>

        {/* Module tabs + Status filter */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { key: 'petty-cash', label: 'Petty Cash',        icon: <Banknote size={15} />,     pending: pendingPetty,    color: '#22D3A5' },
            { key: 'purchase',   label: 'Purchase Requests', icon: <ShoppingCart size={15} />, pending: pendingPurchase, color: '#F59E0B' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setStatusFilter('Pending'); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 12, background: activeTab === tab.key ? '#111827' : 'transparent', border: activeTab === tab.key ? `1px solid ${tab.color}40` : '1px solid rgba(255,255,255,0.08)', color: activeTab === tab.key ? '#fff' : 'rgba(255,255,255,0.45)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              <span style={{ color: activeTab === tab.key ? tab.color : 'rgba(255,255,255,0.3)' }}>{tab.icon}</span>
              {tab.label}
              {tab.pending > 0 && (
                <span style={{ background: tab.color, color: '#080C18', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 900 }}>{tab.pending}</span>
              )}
            </button>
          ))}

          {/* Status filter */}
          <div style={{ marginLeft: 'auto', display: 'flex', background: '#111827', padding: '6px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)' }}>
            {['Pending', 'Approved', 'Rejected', 'All'].map(f => (
              <button key={f} onClick={() => setStatusFilter(f)} style={{ background: statusFilter === f ? '#1F2937' : 'transparent', color: statusFilter === f ? '#fff' : 'rgba(255,255,255,0.4)', border: 'none', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px', color: 'rgba(255,255,255,0.3)' }}>Loading requests...</div>
        ) : (activeTab === 'petty-cash' ? filteredPetty : filteredPurchase).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: '#0D1120', borderRadius: 16, border: '1px dashed rgba(255,255,255,0.1)' }}>
            {activeTab === 'petty-cash'
              ? <Banknote size={40} color="rgba(255,255,255,0.1)" style={{ marginBottom: 15 }} />
              : <ShoppingCart size={40} color="rgba(255,255,255,0.1)" style={{ marginBottom: 15 }} />
            }
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
              No {statusFilter} {activeTab === 'petty-cash' ? 'Petty Cash' : 'Purchase'} Requests
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {activeTab === 'petty-cash'
              ? filteredPetty.map(req => (
                  <PettyCashCard key={req.id} req={req} getJobName={getJobName} formatTime={formatTime} onStatus={handlePettyCashStatus} />
                ))
              : filteredPurchase.map(req => (
                  <PurchaseCard key={req.id} req={req} formatTime={formatTime} onStatus={handlePurchaseStatus} onComment={handlePurchaseComment} />
                ))
            }
          </div>
        )}

      </div>
    </div>
  );
};

export default FinanceDashboard;